import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../utility/useAuth";
import { useStoreActions, useStoreState } from "../../store";
import {
  isSupportAssistantEnabled,
  shouldShowSupportLauncher,
} from "../../utility/supportAssistantAccess";
import {
  askSupportAssistant,
  rateSupportAnswer,
  REQUEST_CANCELLED,
  SupportActionRef,
  SupportCard,
  SupportChatTurn,
  SupportImage,
  SupportLinkRef,
  SupportRating,
  SupportNextRef,
  SupportSettingsRef,
} from "../../services/ApiService";
import AnswerText, { stripAnswerMarkers } from "./AnswerText";
import { logger } from "../../utility/logger";
import DiagnosticModal, { prefillOpenReport } from "../Modals/DiagnosticModal/DiagnosticModal";
import SupportLauncherButton from "./SupportLauncherButton";
import type { ReportPrefill } from "../Modals/DiagnosticModal/DiagnosticModal";
import {
  canReachScreen,
  pathForScreen,
  screenFor,
  screenForPath,
  ScreenDescriptor,
} from "../../utility/routes";
import { supportActionFor } from "../../utility/supportActions";
import type { SupportActionDescriptor } from "../../utility/supportActions";
import {
  supportSettingsFields,
  supportSettingsFor,
} from "../../utility/supportSettings";
import type { SupportSettingsDescriptor } from "../../utility/supportSettings";
import { requestJobAction } from "../../services/supportActionBus";
import { requestSettingsPrefill } from "../../services/supportSettingsBus";
import { onSupportAssistantOpen } from "../../utility/supportAssistantBus";
import { useAccess } from "../../access/AccessProvider";
import ICON_X from "../../Icons/X.svg";
import ICON_THUMB_UP from "../../Icons/ThumbUp.svg";
import ICON_THUMB_DOWN from "../../Icons/ThumbDown.svg";
import COSMO_LOGO from "../../Icons/CosmoLogo.png";
import {
  Bubble,
  Chip,
  ChipRow,
  ClearButton,
  CloseButton,
  Composer,
  ContextRow,
  Disclaimer,
  EscalateButton,
  EscalateRow,
  HeaderActions,
  HeaderLogo,
  Input,
  Intro,
  Notice,
  Panel,
  PanelHeader,
  RateButton,
  RateRow,
  SendButton,
  Thread,
} from "./SupportAssistant.styles";

/**
 * The in-app support assistant: the chat panel, mounted once at the top of the app by
 * `SupportAssistantHost`.
 *
 * IT DRAWS ITS OWN LAUNCHER AGAIN (2026-10-01): `SupportLauncherButton`, a floating bubble at
 * the panel's bottom-right anchor, rendered whenever the panel is closed. It left the app
 * header because the CoSMo mark was unreadable at the header's 16px. Other callers
 * (`ErrorModal`) still come in through `utility/supportAssistantBus`. The panel is fixed
 * bottom-right, steps clear of the feedback drawer's overlay (top-aligned with the drawer's
 * form while it is open), and closes on Escape.
 *
 * GATING. `isSupportAssistantEnabled()` is checked FIRST and returns null, so when the
 * Key Vault flag is off nothing is rendered — no panel, no icon — and no network call can be
 * made. That check is the whole feature switch; there is no second place to turn it off.
 *
 * The BFF has its own, independent gate (503 when this environment has no model deployment).
 * The two are allowed to disagree: flag off means nothing exists, and flag on with an
 * unconfigured BFF means the panel says so once and then stops rendering for the session
 * rather than answering the launcher with something that can only fail. That separation is
 * deliberate — the client and server halves of SupportAutoTenantEnabled had to agree, drifted,
 * and broke the feature outright.
 */
/**
 * A turn as the PANEL holds it: what the BFF needs back, plus the pictures that came with it
 * and the rating the reader gave it.
 *
 * ⚠️ NONE OF THE EXTRAS MAY GO BACK TO THE SERVER. `history` is posted with every subsequent
 * question, and a screenshot is ~30–160KB of base64 — so a turn carrying its images would
 * re-upload every picture in the conversation on every question after it, growing quadratically
 * and dwarfing the download this feature was already careful about. `submit` maps history down
 * to `{ role, content }` for exactly this reason; keep it that way.
 */
type ThreadTurn = SupportChatTurn & {
  images?: SupportImage[];

  /**
   * The order cards drawn inside this answer, in slot order.
   *
   * ⚠️ THESE MUST NOT TRAVEL BACK IN `history` EITHER, and here the reason is CORRECTNESS as
   * well as bytes: a card is a snapshot of an order at the moment it was looked up. Replayed
   * into a later question it is data the model would narrate as current, and "quantity
   * completed" on a live job is exactly the figure that moves between two questions in one
   * conversation. The `{ role, content }` map in `submit` already covers it — by construction,
   * which is the guarantee, rather than by anyone remembering to strip a new field.
   */
  cards?: SupportCard[];

  /**
   * The chips offered under this answer.
   *
   * ⚠️ THESE ARE EXTRAS AND THEY MUST NOT TRAVEL BACK IN `history` EITHER. `submit` maps the
   * thread down to `{ role, content }`, which covers images, chips and anything added later by
   * construction rather than by remembering. Keep it that way — the map is the guarantee, not
   * the list of things to strip.
   */
  links?: SupportLinkRef[];
  actions?: SupportActionRef[];
  next?: SupportNextRef[];
  settings?: SupportSettingsRef[];

  /**
   * The recorded exchange this answer belongs to. Absent when the BFF recorded nothing —
   * transcripts switched off, a storage failure, or a Function App that predates the feature —
   * and the thumbs are then not offered at all rather than offered and guaranteed to fail.
   */
  id?: string;

  /** Optimistic: set the moment the thumb is pressed, never read back from the server. */
  rating?: SupportRating;
};

/**
 * What the panel says when a thumbs-down opens the report form.
 *
 * The form appears over the app on its own, filled in with things the user never typed. Without
 * a line in the thread saying so, a modal that opens by itself reads as the thumb having
 * SUBMITTED something — and the user closes it, which is exactly the signal we were trying to
 * collect. It also has to say nothing is sent yet, because nothing is: the form pre-fills and
 * waits for them.
 *
 * A constant so `rate` can tell its own notice apart from an error message when the rating is
 * taken back — see the withdrawal branch there.
 */
const ESCALATION_NOTICE =
  "Sorry that didn't help. I've opened a report for support and filled in your question and " +
  "this conversation — add anything that would help, then send it. Nothing goes to support " +
  "until you do.";

/**
 * What the panel says when the Submit Feedback drawer opened it.
 *
 * Same lesson as `ESCALATION_NOTICE`, in a milder form: something the user did not ask for has
 * appeared next to the form they were filling in, and a panel that turns up unbidden reads as
 * having DONE something. Two sentences: why it is here, and — the part that actually matters —
 * that it is not in the path of their feedback. Nothing they type in here reaches support, and
 * the form beside it is exactly as they left it.
 *
 * ⚠️ IT IS WRITTEN ONLY WHEN THE PANEL WAS CLOSED. A panel that was already open has not
 * appeared and has nothing to explain — and the drawer opens over an ALREADY-OPEN panel on the
 * thumbs-down path, where this would land on top of `ESCALATION_NOTICE` and replace the one
 * sentence that says the report has not been sent. See the auto-open effect.
 *
 * A constant so the intro can recognise it — it is the one notice that does not replace the
 * intro, because it is about the panel rather than about the conversation.
 */
const DRAWER_OPEN_NOTICE =
  "I've opened in case it's quicker to ask me first. Nothing you type in here goes to " +
  "support, and your feedback form is still there, exactly as you left it.";

/** A fresh conversation id, grouping the exchanges of one thread in the BFF's record. */
const newConversationId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `c-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * The production order named by the CURRENT PATH, if the screen is about one.
 *
 * ⚠️ READ FROM THE PATH, NOT FROM `useParams()`. The panel is mounted by
 * `SupportAssistantHost`, a SIBLING of the router's `<Routes>` element rather than a
 * descendant of any route — which is the entire point of the hoist, since a lazy page
 * suspending below would otherwise destroy the panel and the conversation in it. Outside a
 * matched route `useParams()` returns `{}`, silently and always, so it would read as "no order
 * on screen" on the one screen that always has one.
 *
 * `screenForPath` decides WHETHER this path carries an order; the segment index is safe
 * because every parameterised screen in the table is `<static>/:param`.
 */
const orderInPath = (pathname: string): string | null => {
  const screen = screenForPath(pathname);
  if (!screen || screen.param !== "order") return null;

  const raw = (pathname || "").split("?")[0].split("/").filter(Boolean)[1] ?? "";
  if (!raw) return null;

  try {
    // `pathForScreen` encodes on the way out, so decode on the way back in — an order number
    // may legitimately contain a `/`, which is exactly why it was encoded.
    return decodeURIComponent(raw).trim() || null;
  } catch {
    return raw.trim() || null;
  }
};

/** Why the report form is being offered, and for which question. See `escalation` below. */
type Escalation = { question: string; reason: "unanswered" | "unhelpful" };

/**
 * What the report form starts with when the assistant could not answer. It carries the
 * question and the thread so support sees what was actually asked — but it only PRE-FILLS.
 * Nothing is sent until the user reviews it and submits it themselves.
 *
 * A pure function, not only a memo, because `openReport` needs it for an escalation set in
 * the SAME handler: handing the report to an already-open drawer cannot wait for a render.
 */
const escalationReport = (
  escalation: Escalation,
  turns: ThreadTurn[],
  diagnostics: string | null
): ReportPrefill => {
  // The assistant's turns keep their markers — that is how the panel positions the
  // screenshots and knows which chips to offer — but this transcript is read as plain text in
  // an e-mail, where a marker is noise to the support engineer reading it.
  const transcript = turns
    .map(
      (t) =>
        `${t.role === "user" ? "Me" : "CoSMo"}: ${
          t.role === "user" ? t.content : stripAnswerMarkers(t.content)
        }`
    )
    .join("\n\n");

  const opening =
    escalation.reason === "unhelpful"
      ? `CoSMo answered this, and the answer did not help:\n\n"${escalation.question}"\n\n`
      : `I asked CoSMo and it could not answer:\n\n"${escalation.question}"\n\n`;

  // Below the user's own words, never instead of them: whoever wrote this question is
  // describing the problem, and the raw text of the error is evidence, not the report.
  const context = diagnostics ? `\n\n--- What the app reported ---\n${diagnostics}` : "";

  return {
    subject:
      escalation.reason === "unhelpful"
        ? `CoSMo's answer didn't help: ${escalation.question}`
        : `CoSMo couldn't answer: ${escalation.question}`,
    description:
      opening +
      `Please describe what you were trying to do:\n\n\n` +
      `--- Conversation ---\n${transcript}` +
      context,
  };
};

const SupportAssistant: FC = () => {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isManagerMode = useStoreState((s) => s.appSettings.isManagerMode);
  const isDiagnosticPaneOpen = useStoreState((s) => s.appSettings.isDiagnosticPaneOpen);

  /**
   * What the user may do, from `GET api/me`, passed straight to `canReachScreen`. Access is read
   * through `utility/routes.ts` so there is exactly one answer to keep correct, and the panel
   * never reads the role bits in the store.
   */
  const { can } = useAccess();
  const startedJob = useStoreState((s: any) => s.appSettings?.startedJob);

  const setIsManagerMode = useStoreActions((a: any) => a.appSettings?.setIsManagerMode);
  const setIsActiveJobMode = useStoreActions((a: any) => a.appSettings?.setIsActiveJobMode);

  /** Which screen the user is on, as a key the Function App understands. */
  const contextScreen = screenForPath(location.pathname);

  /**
   * The production order the user is looking at RIGHT NOW — the one the route names, and
   * nothing else.
   *
   * ⚠️ IT DELIBERATELY DOES NOT FALL BACK TO `appSettings.jobNumber`, AND THAT FALLBACK WAS A
   * BUG. That store field is written by `JobPage`, `BomPage` and `RoutingPage` when they mount
   * and is never cleared on the way out — and the store is persisted to sessionStorage, so it
   * outlives navigation AND a reload. Falling back to it meant that once anybody had opened a
   * single production order, the panel believed that order was on screen for the rest of the
   * session, everywhere.
   *
   * Three things went wrong off the back of that, in rising order of seriousness:
   *   • "Diagnose this order (4501234)" sat above the composer on every screen and could not be
   *     got rid of — Clear chat does not touch it, because it is not conversation state. That is
   *     how this was found.
   *   • `productionOrder` in the session context told the model an order was on screen when it
   *     was not, so "open its parts list" resolved against a stale number.
   *   • A link or ACTION chip with no parameter of its own falls back to this value. An action
   *     chip is a dialog opener aimed at a specific production order, so a stale fallback could
   *     point the rework or complete form at an order the conversation never mentioned.
   *
   * "The job you are working on" is a real and useful fact, but it is a DIFFERENT fact and it
   * already travels separately as `clockedInJob`, taken from `startedJob`, which is maintained
   * properly. Nothing is lost by removing this fallback.
   */
  const contextOrder = orderInPath(location.pathname);

  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<ThreadTurn[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  /**
   * Set once the BFF answers 503. The feature is configured in the vault but not on this
   * environment's Function App, so there is nothing the user can do — stop offering it for
   * the rest of the session instead of letting them ask again and fail again.
   */
  const [unavailable, setUnavailable] = useState(false);

  /**
   * Why we are offering the report form, and for which question — handed to the form when
   * they escalate.
   *
   * Two things land here now. `unanswered` is the assistant admitting it has nothing (or the
   * request failing outright); `unhelpful` is the reader pressing thumbs-down on an answer it
   * did give. They read completely differently to the support engineer who gets the mail — "it
   * couldn't answer" versus "it answered and the answer was wrong" — so the reason travels
   * with the question rather than being inferred at the other end.
   */
  const [escalation, setEscalation] = useState<Escalation | null>(null);
  const [reportOpen, setReportOpen] = useState(false);

  /**
   * Context that arrived WITH a seeded question and that the user never typed — today the raw
   * text of the error an `ErrorModal` was showing.
   *
   * It is deliberately kept out of the composer: the user should see, and be able to edit, the
   * question in their own words. It rides along to the escalation report instead, where a
   * support engineer is the one who wants the raw message.
   */
  const [diagnostics, setDiagnostics] = useState<string | null>(null);

  /**
   * Groups this thread's exchanges in the BFF's record, so a conversation can be read back in
   * order rather than as a pile of unrelated questions.
   *
   * A ref, not state: nothing renders from it, and it must be readable by `submit` without
   * adding a dependency that re-creates the handler. Clearing the chat mints a new one — a
   * cleared thread is a new conversation by definition, and reusing the id would silently
   * staple two unrelated ones together in the record.
   */
  const conversationId = useRef<string>(newConversationId());

  const threadRef = useRef<HTMLDivElement>(null);

  /**
   * How long the current question has been running, in seconds.
   *
   * A guide answer returns in about a second, but a manager's data question runs several
   * model round trips while it looks things up. A motionless "Thinking…" reads as a hung
   * screen, and the user asks again — which costs another exchange. The elapsed count drives
   * a message that changes, so it visibly progresses.
   */
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!sending) {
      setElapsed(0);
      return;
    }

    const started = Date.now();
    const timer = window.setInterval(
      () => setElapsed(Math.floor((Date.now() - started) / 1000)),
      1000
    );

    return () => window.clearInterval(timer);
  }, [sending]);

  // Deliberately vague about mechanism — the prompt forbids the answer describing how the
  // system works, and the progress line should not do it either.
  const progressMessage =
    elapsed < 4 ? "Thinking…" : elapsed < 12 ? "Looking this up…" : "Still working…";

  // Keep the newest message in view as the conversation grows.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, sending, notice]);

  /**
   * Start again: empty thread, new conversation id, and every trace of the last exchange
   * cleared with it. `message` leaves a line behind in the empty panel — used by the thumbs-up
   * path, which clears on the user's behalf and would otherwise blank the screen with no
   * explanation.
   *
   * ⚠️ THE ESCALATION STATE HAS TO GO TOO. `escalation` drives the "Report this to support"
   * button AND seeds the report form; left behind, a cleared panel would still offer to report
   * a question that is no longer on screen, and the form would open pre-filled with a
   * conversation the user has just decided to get rid of.
   *
   * ⚠️ DECLARED ABOVE THE EARLY RETURNS because the close effect below calls it, and that effect
   * runs even on a render that returned null (`unavailable && !open` is exactly such a render).
   * A `const` declared under the returns would not exist yet there.
   *
   * The BFF holds nothing to clear — the client owns the thread, and the recorded transcripts
   * are a separate, deliberate record that clearing the panel is not a request to delete.
   */
  const clearChat = (message: string | null = null) => {
    setTurns([]);
    setDraft("");
    setNotice(message);
    setEscalation(null);
    // Same reasoning as `escalation`: it seeds the report form, and a cleared panel must not
    // carry the error from a conversation the user has just discarded into the next one.
    setDiagnostics(null);
    conversationId.current = newConversationId();
  };

  /**
   * CLOSING THE PANEL CLEARS THE CHAT — whatever closed it (the X, Escape, or anything added
   * later). Watched as the open→closed TRANSITION rather than wired into each close handler, so
   * a new way to close cannot forget to clear. Reopening always starts an empty conversation
   * with a new id; the half-typed draft goes with it.
   *
   * An answer still in flight when the panel closes is dropped on arrival — see the
   * conversation-id check in `submit`.
   */
  const panelWasOpen = useRef(open);

  useEffect(() => {
    const wasOpen = panelWasOpen.current;
    panelWasOpen.current = open;

    if (wasOpen && !open) clearChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Escape closes the panel, matching every other dialog in the app.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  /**
   * OPENING THE SUBMIT FEEDBACK DRAWER OPENS THE PANEL WITH IT.
   *
   * Somebody reaching for the feedback form has a problem they have not been able to solve, and
   * the assistant is the faster answer for most of what gets reported through it. The drawer is
   * the one moment we know that for certain, so the panel puts itself where they can see it
   * rather than waiting to be found. The drawer's own overlay is full-viewport, which is why
   * `Panel` steps out from under it (`$offset`) — the two are meant to be on screen together.
   *
   * ⚠️ ON THE TRANSITION ONLY, VIA `drawerWasOpen`. Reopening on every render while the drawer
   * is open would mean a user who opens the drawer, closes the panel and carries on typing gets
   * it pushed back in front of them — which is worse than never opening it, because it cannot be
   * got rid of. Closing the panel has to stick for as long as that drawer is open.
   *
   * ⚠️ THE REF STARTS FROM THE CURRENT VALUE, so a panel that MOUNTS with the drawer already
   * open sees no transition and stays shut. That is not a theoretical case: the flag lives in
   * the store, the store persists to sessionStorage, and a reload with the drawer open runs no
   * unmount cleanup to clear it — so "open on mount" would have the panel greet some users on
   * every page load.
   *
   * ⚠️ AND IT NEVER CLOSES THE PANEL. Opening is a nudge; closing is the user's decision, and a
   * half-finished conversation must not be yanked away because they dismissed the form.
   *
   * ⚠️ THE FLAG IS CHECKED HERE, for the same reason the bus subscription below checks it: the
   * early returns sit under every hook, so this effect runs even where the panel renders nothing.
   * `setOpen(true)` on a component that returns null is invisible but still wrong — it would
   * leave `open` true, and `unavailable && !open` is a real render condition.
   */
  const drawerWasOpen = useRef(isDiagnosticPaneOpen);

  useEffect(() => {
    const wasOpen = drawerWasOpen.current;
    drawerWasOpen.current = isDiagnosticPaneOpen;

    if (!isSupportAssistantEnabled()) return;
    if (!isDiagnosticPaneOpen || wasOpen) return;

    setOpen(true);

    // Only when it actually APPEARED. An open panel has not turned up out of nowhere and owes
    // no explanation — and this is what keeps the thumbs-down path intact, where `rate` has just
    // written `ESCALATION_NOTICE` and then opens the very drawer this effect watches.
    if (!open) setNotice(DRAWER_OPEN_NOTICE);

    // `open` is read, so it is a dependency. Re-running on it is harmless: the ref has already
    // been advanced to `true` by the run that opened the panel, so the user closing it produces
    // a run that falls straight out at `wasOpen`.
  }, [isDiagnosticPaneOpen, open]);

  /**
   * THE WAY IN FROM THE REST OF THE APP (`utility/supportAssistantBus`).
   *
   * ⚠️ THIS EFFECT CHECKS THE FEATURE FLAG ITSELF, AND THAT IS NOT BELT-AND-BRACES.
   * The component's early returns sit BELOW every hook (they have to — hooks cannot be
   * conditional), so this effect runs even in an environment where the flag is off and the
   * panel renders nothing. Registering there would make `isSupportAssistantMounted()` answer
   * `true` for a panel that does not exist, and `ErrorModal` would render "Ask the assistant
   * about this" as a button that silently does nothing. The flag has to be asked here, where
   * the registration happens.
   *
   * ⚠️ SEEDING PRE-FILLS; IT DOES NOT SEND. The panel's whole ethic is that the user presses
   * the button — the escalation form pre-fills and waits, the action chips open a modal and
   * wait, and a question somebody else wrote on their behalf is no different. They may want to
   * change the wording, and a question that sent itself would also spend a model call they
   * never asked for.
   *
   * ⚠️ NO SEED MEANS OPEN IT AND TOUCH NOTHING — that is the launcher bubble
   * (`SupportLauncherButton`), which has no question to offer. Every line below the first is a
   * REPLACEMENT, and replacing is right when the caller is opening the panel about something
   * specific: a new question from an `ErrorModal` is a new subject, so the composer, the
   * diagnostics and the last notice all belong to what came before it. (Nothing survives a close
   * any more — closing clears the chat — so the launcher always opens an empty panel; this only
   * matters for a seed arriving while the panel is already open.)
   */
  useEffect(() => {
    if (!isSupportAssistantEnabled()) return;

    return onSupportAssistantOpen((seed) => {
      setOpen(true);
      if (!seed) return;

      setDraft(seed.question ?? "");
      setDiagnostics(seed.diagnostics?.trim() || null);
      // Whatever was last said in here is about the previous conversation; the user has just
      // arrived from somewhere else with a new problem.
      setNotice(null);
    });
  }, []);

  const report = useMemo(
    () => (escalation ? escalationReport(escalation, turns, diagnostics) : null),
    [escalation, turns, diagnostics]
  );

  if (!isSupportAssistantEnabled()) return null;
  if (unavailable && !open) return null;

  /**
   * Ask a question.
   *
   * ⚠️ `questionOverride` IS NOT A CONVENIENCE — IT IS THE FIX FOR A STALE CLOSURE.
   * This used to read `draft` out of state, so the only way to ask programmatically was
   * `setDraft(q)` followed by `submit()`. That reads the `draft` captured when the handler was
   * created, which is the value from BEFORE the `setDraft` — i.e. the previous question, or an
   * empty string and no request at all. It is the classic React trap, and it is worse than
   * usual here because it passes in a test: an `await` between the two lets React flush the
   * state update and re-create the handler, so an awaited test sees the new value and a real
   * click never does.
   *
   * Everything that asks now says WHAT it is asking: the form and the Enter key pass
   * `draft.trim()` explicitly, a follow-up chip passes its own question, and the diagnose chip
   * passes the sentence it builds. `draft` remains the default only so no caller is required
   * to pass anything.
   */
  const submit = async (e?: React.FormEvent, questionOverride?: string) => {
    e?.preventDefault();

    const question = (questionOverride ?? draft).trim();
    if (!question || sending) return;

    // The question joins the thread immediately; the history sent to the BFF is what came
    // BEFORE it, since the question travels as its own field.
    //
    // Mapped down to role and content: the pictures stay in the browser. See ThreadTurn.
    const history: SupportChatTurn[] = turns.map((t) => ({ role: t.role, content: t.content }));
    setTurns([...turns, { role: "user", content: question }]);
    setDraft("");
    setNotice(null);
    setSending(true);

    // Closing the panel (or Clear chat) mid-question mints a new id. An answer that lands after
    // that belongs to a conversation the user has discarded, and must not reappear in the next.
    const askedIn = conversationId.current;

    try {
      const result = await askSupportAssistant(
        auth,
        question,
        history,
        askedIn,
        {
          // Keys and identifiers only — see SupportChatContext. The Function App decides what
          // to offer from these; this client decides, independently, what it will render.
          screenKey: contextScreen?.key,
          productionOrder: contextOrder ?? undefined,
          clockedInJob: startedJob?.jobNumber ?? undefined,
        }
      );

      if (conversationId.current !== askedIn) return;

      switch (result.kind) {
        case "answer":
          setTurns((prev) => [
            ...prev,
            {
              role: "assistant",
              content: result.answer,
              images: result.images,
              cards: result.cards,
              id: result.id,
              links: result.links,
              actions: result.actions,
              next: result.next,
              settings: result.settings,
            },
          ]);
          setEscalation(
            result.escalate ? { question, reason: "unanswered" } : null
          );
          break;

        case "unavailable":
          setUnavailable(true);
          setNotice(
            "CoSMo isn't switched on for this site yet. Please contact support."
          );
          break;

        case "busy":
          setNotice("CoSMo is busy right now. Please try again in a moment.");
          break;

        default:
          setNotice(
            "Something went wrong reaching CoSMo. Please try again, or contact support."
          );
          setEscalation({ question, reason: "unanswered" });
      }
    } catch (error) {
      if ((error as Error)?.message !== REQUEST_CANCELLED) {
        logger.warn("Support assistant request failed.", error);
        setNotice("Something went wrong. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  // Enter sends, Shift+Enter makes a new line — the convention users expect of a chat box.
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit(undefined, draft.trim());
    }
  };

  /**
   * Put the report in front of the user — ONE report form, never two.
   *
   * ⚠️ IF THE FOOTER'S SUBMIT FEEDBACK DRAWER IS ALREADY OPEN, THE REPORT GOES INTO IT. Opening
   * this component's own drawer as well stacked a second one on top, and closing either cleared
   * `isDiagnosticPaneOpen` while the other was still up. `prefillOpenReport` merges into the
   * open drawer rather than replacing what the user already typed there. `!reportOpen` keeps our
   * own drawer's second press (it is already open, already seeded) on the ordinary path.
   */
  const openReport = (esc: Escalation) => {
    if (
      isDiagnosticPaneOpen &&
      !reportOpen &&
      prefillOpenReport(escalationReport(esc, turns, diagnostics))
    ) {
      return;
    }

    setReportOpen(true);
  };

  /** The question that produced the answer at `index` — what a report is actually about. */
  const questionFor = (index: number): string => {
    for (let i = index - 1; i >= 0; i--) {
      if (turns[i].role === "user") return turns[i].content;
    }

    return "";
  };

  /**
   * Thumbs up/down on one answer, and the two things that follow from it.
   *
   * A thumb is the end of the exchange, not a footnote to it, so it is treated as one:
   *
   * • UP clears the thread. The reader got what they came for; leaving the answered question
   *   on screen only means the next person to ask something starts by scrolling past it — and
   *   a cleared thread is a new conversation id, which keeps the record honest.
   * • DOWN opens the report form, pre-filled with the conversation, and leaves a line in the
   *   thread saying so. Someone who says the answer was wrong has already told us they need a
   *   human; making them then find the feedback drawer and retype what they just asked is how
   *   the signal gets lost. The line is not decoration — a form that opens by itself, already
   *   filled in, otherwise reads as the thumb having sent something.
   *
   * Taking a rating back (pressing the lit thumb again) does neither — it is a correction, not
   * a verdict.
   *
   * ⚠️ OPTIMISTIC AND UNCONDITIONAL. The rating is recorded fire-and-forget whatever the server
   * says: `rateSupportAnswer` resolves false rather than throwing, and nothing here reads the
   * result. A failed rating is worth exactly nothing to the person who pressed it, and an error
   * message over an answer they were happy with is worse than losing the signal. The clear and
   * the report form must not wait on it either — they are the user's business, not the
   * server's.
   */
  const rate = (index: number, rating: SupportRating) => {
    const turn = turns[index];
    if (!turn?.id) return;

    const next: SupportRating = turn.rating === rating ? "none" : rating;

    setTurns((prev) =>
      prev.map((t, i) => (i === index ? { ...t, rating: next } : t))
    );

    void rateSupportAnswer(auth, turn.id, next);

    if (next === "up") {
      clearChat("Glad that helped. Ask me something else whenever you need to.");
      return;
    }

    if (next === "down") {
      // Set together, and read in the same render: the form seeds itself from these props the
      // moment `open` turns true.
      const esc: Escalation = { question: questionFor(index), reason: "unhelpful" };
      setEscalation(esc);
      openReport(esc);
      // Left in the thread behind the form, so it is still there when they close it — that is
      // the moment they need to know what just happened and that nothing was sent.
      setNotice(ESCALATION_NOTICE);
      return;
    }

    // Withdrawing the thumbs-down takes the explanation with it; the form it described is no
    // longer the thing on screen. Conditional because `notice` is shared with the error
    // messages — an earlier "something went wrong" is not ours to clear.
    if (next === "none" && notice === ESCALATION_NOTICE) setNotice(null);
  };

  /* ---------------------------------------------------------------------------------------
   * CHIPS
   *
   * ⚠️ EVERY CHIP RESOLVES THROUGH `utility/routes.ts`, AND A SCREEN `canReachScreen` REFUSES
   * RENDERS NOTHING — even though the Function App has already stripped it.
   *
   * That duplication is the design. The server's list is built from a corpus and a model
   * prompt; this client is the only thing that holds the user's access map for the organisation
   * the session acts in, and whether Analytics is switched on in this environment. The rule is that the assistant
   * must never OFFER a door it knows is locked: an offer that bounces the user back to /home
   * reads as the assistant being wrong, which is worse than it having said nothing. Two
   * independent defences, and neither is allowed to assume the other ran.
   *
   * Nothing below writes to the server, and nothing below submits or saves anything. A link
   * navigates; an action records an intent and navigates; a settings prefill records some VALUES
   * and navigates; a follow-up asks a question. That is the
   * entire vocabulary.
   * ------------------------------------------------------------------------------------- */

  /** The screen that hosts the action modals. Resolved once — it is `job` for all five. */
  const reachable = (screen: ScreenDescriptor | null): screen is ScreenDescriptor =>
    canReachScreen(screen, { can });

  /**
   * Go to a screen the assistant named.
   *
   * ⚠️ MANAGER VIEW IS TURNED ON WHEN THE SCREEN NEEDS IT — AND THE USER IS TOLD.
   * `isManagerMode` is a VIEW, not a permission: a manager in Shop Floor view passes the
   * screen's `CapabilityRoute` but `layouts/Default` renders the Settings ribbon only in Manager
   * view, so they would land on the screen with no way to move between its siblings. The header does the
   * same pair of writes when its Manager tab is clicked (`CSMShopfloorHeader.tsx:119-131`), and
   * `setIsActiveJobMode(true)` is part of that precedent rather than an extra.
   *
   * Never silently: the mode the app is in is the user's, and a help feature that changes it
   * behind their back is how someone ends up not knowing why their board looks different. The
   * line goes in the thread, where they are already reading.
   */
  /**
   * Turn Manager view on for a screen that needs it, and say whether it did.
   *
   * Split out because two callers need the same pair of writes and the same sentence, and the
   * settings chip has a second thing to say afterwards — see `runSettingsPrefill`. The return
   * value is "did anything change", so a caller can decide whether the sentence is owed.
   */
  const enterManagerViewFor = (screen: ScreenDescriptor): boolean => {
    if (!screen.requiresManagerView || isManagerMode) return false;

    setIsManagerMode?.(true);
    setIsActiveJobMode?.(true);
    return true;
  };

  const managerViewNotice = (screen: ScreenDescriptor): string =>
    `${screen.title} is only shown in Manager view, so I've switched you to it. ` +
    "Use the Shop Floor tab at the top to switch back.";

  const openScreen = (screen: ScreenDescriptor, href: string) => {
    if (enterManagerViewFor(screen)) setNotice(managerViewNotice(screen));

    navigate(href);
  };

  /**
   * Screens this answer offers, already filtered to ones this user can actually reach.
   *
   * ⚠️ `ref.key`, NOT `ref.screenKey`. The Function App's `SupportLink`
   * (`Support/AnswerActionCatalog.cs`) serialises `{ key, param, label }` and always has. This
   * read `ref.screenKey` until 2026-09-14, so `screenFor` was handed `undefined` on every real
   * response, the `reachable` filter dropped everything, and NOT ONE LINK CHIP WAS EVER
   * RENDERED — while every test here passed, because every test mocked this file's own shape.
   */
  const linkChipsFor = (turn: ThreadTurn) =>
    (turn.links ?? [])
      .map((ref) => ({ ref, screen: screenFor(ref.key) }))
      .filter((c): c is { ref: SupportLinkRef; screen: ScreenDescriptor } =>
        reachable(c.screen)
      )
      .map((c) => ({
        ...c,
        // A parameterised screen falls back to the order the user is already looking at, so an
        // answer can say "open the parts list" without repeating the number back at itself.
        href: pathForScreen(c.screen, c.ref.param ?? contextOrder),
      }))
      .filter((c): c is typeof c & { href: string } => Boolean(c.href));

  /**
   * Modals this answer offers to OPEN, pre-filled.
   *
   * The host screen is resolved through the screen table like any other navigation, so an
   * action is subject to exactly the same access gate — there is no second way in.
   *
   * ⚠️ THE ORDER NUMBER IS `ref.param`. It read `ref.productionOrder` until 2026-09-14, which
   * the Function App has never sent (`SupportAction` there is `{ key, param, label, kind,
   * hostScreen }`), so it was always `undefined` and always fell back to whatever order the
   * user happened to be looking at — right by luck on the job screen, wrong or empty
   * everywhere else. A rework dialog opened on the wrong production order is the worst thing
   * this feature can do.
   *
   * ⚠️ AND `kind` IS CHECKED RATHER THAN TRUSTED. The Function App's own comment on that field
   * reads "always openModal, so the client can refuse anything else" — so this actually
   * refuses. An absent kind is accepted (an older Function App sent none); a PRESENT one that
   * is not `openModal` renders nothing. The whole product rule is that the assistant opens
   * forms and never submits them, and that rule should not rest on a field nobody reads.
   */
  const actionChipsFor = (turn: ThreadTurn) =>
    (turn.actions ?? [])
      .filter((ref) => !ref.kind || ref.kind === "openModal")
      .map((ref) => ({ ref, action: supportActionFor(ref.key) }))
      .filter((c) => Boolean(c.action))
      .map((c) => ({
        ...c,
        action: c.action!,
        host: screenFor(c.action!.hostScreen),
        order: (c.ref.param ?? contextOrder ?? "").trim(),
      }))
      .filter((c) => Boolean(c.order) && reachable(c.host))
      .map((c) => ({ ...c, href: pathForScreen(c.host as ScreenDescriptor, c.order) }))
      .filter((c): c is typeof c & { href: string } => Boolean(c.href));

  /**
   * Run the action chip: record the intent, go to the screen. NOTHING ELSE.
   *
   * The intent is a key and a production order. It cannot carry a quantity, a reason or a
   * resource, because it is not a request to do anything — `pages/JobPage/useSupportJobAction`
   * claims it, re-checks that screen's own guards, and at most opens a form. The user commits.
   *
   * ⚠️ A DIALOG THAT WILL ASK SOMETHING UNEXPECTED SAYS SO FIRST. `start-job` on an operator
   * who is already clocked onto a different production order opens a question about stopping
   * THAT one (`JobAction.tsx:202-216`). They still answer it themselves, so nothing is
   * submitted on their behalf — but the chip was about this order, and being handed a question
   * about another is a surprise the panel can cheaply remove. Same `Notice`, same reasoning, as
   * announcing a Manager-view flip: the line goes where they are already reading.
   */
  const runAction = (chip: {
    action: SupportActionDescriptor;
    order: string;
    href: string;
  }) => {
    if (chip.action.caution) setNotice(chip.action.caution);

    requestJobAction({ key: chip.action.key, jobNumber: chip.order });
    navigate(chip.href);
  };

  /**
   * Create forms this answer offers to FILL IN, on a settings screen.
   *
   * Resolved through the same screen table as everything else, so a manager-only settings screen
   * is access-gated identically — and `canReachScreen` is asked here even though the Function
   * App has already stripped the marker for anyone who cannot reach it. Neither side may assume
   * the other ran.
   *
   * ⚠️ AND THE FIELDS ARE FILTERED AGAIN HERE, against this build's own list. The server filters
   * against its schema; this filters against `supportSettings.ts`. It matters more than it looks
   * — these values are spread into a page's form state, so an unexpected field name is a key
   * written into `FormState`. A prefill with nothing left is not rendered: a chip that navigates
   * somewhere and presses Add on an empty form leaves a row the user has to go and delete.
   */
  const settingsChipsFor = (turn: ThreadTurn) =>
    (turn.settings ?? [])
      .map((ref) => ({ ref, settings: supportSettingsFor(ref.key) }))
      .filter((c) => Boolean(c.settings))
      .map((c) => ({
        ref: c.ref,
        settings: c.settings!,
        screen: screenFor(c.settings!.hostScreen),
        fields: supportSettingsFields(c.settings!, c.ref.fields),
      }))
      .filter((c) => Object.keys(c.fields).length > 0 && reachable(c.screen))
      .map((c) => ({
        ...c,
        screen: c.screen as ScreenDescriptor,
        href: pathForScreen(c.screen as ScreenDescriptor),
      }))
      .filter((c): c is typeof c & { href: string } => Boolean(c.href));

  /**
   * Run the settings chip: record the values, go to the screen, SAY NOTHING IS SAVED.
   *
   * ⚠️ THE THIRD LINE IS NOT POLITENESS. A form that fills itself in reads, to the person
   * watching it happen, exactly like a form that has just been submitted — and this one is on a
   * screen where the alternative reading ("the user now exists") is something they will act on
   * by walking away. The app's own vocabulary already says *"User \"X\" added (not yet saved)."*
   * in a toast that lasts two seconds; the thread is where the sentence stays put. Same
   * `Notice`, same reasoning, as announcing a Manager-view flip and as the escalation notice.
   *
   * The button really is called **Update** by the time they get there: both settings screens
   * label it `isDirty ? "Update" : "OK"`, and the page marks itself dirty as part of adding the
   * row (`UsersPage.tsx:1194`, `OperationsPage.tsx:598`). Saying "Save" would send somebody
   * looking for a button that is not on the screen.
   */
  const runSettingsPrefill = (chip: {
    settings: SupportSettingsDescriptor;
    screen: ScreenDescriptor;
    fields: Record<string, string>;
    href: string;
  }) => {
    const flipped = enterManagerViewFor(chip.screen);

    // Recorded BEFORE navigating, so the page that is already mounted hears it on the bus and
    // the page that is not has it waiting. Nothing about this call writes anything.
    requestSettingsPrefill({ key: chip.settings.key, fields: chip.fields });

    setNotice(
      flipped
        ? `${managerViewNotice(chip.screen)} ${chip.settings.unsavedNotice}`
        : chip.settings.unsavedNotice
    );

    navigate(chip.href);
  };

  /**
   * What this PERSON may do — which is not the same question as whether the app is in Manager
   * view.
   *
   * ⚠️ `isManagerMode` IS A VIEW; THESE ARE PERMISSIONS. A manager standing on the shop-floor
   * board is still a manager, and the server gates its data tools on capabilities — never on
   * which screen the client happens to be showing. Anything in this panel that describes or
   * offers a server-side capability has to ask the map, or it will tell a manager they cannot
   * do something they just did.
   *
   * Each of the two lookups that are not open to everyone is named only for a user who holds
   * the key the server asks for it: completed work needs `buildReports`, what another person
   * is working on needs `viewUsers`. The examples follow the Manager tab (`manageSettings`).
   */
  const offersSettingsExamples = can("manageSettings");
  const lookups = [
    "a production order, its parts and notes",
    "what's open on the board",
    ...(can("buildReports") ? ["what was completed in a period"] : []),
    ...(can("viewUsers") ? ["what someone is working on"] : []),
  ];
  const lookupSentence = `I can also look up ${lookups.slice(0, -1).join(", ")}, or ${
    lookups[lookups.length - 1]
  }. I can't change anything.`;

  /**
   * "Diagnose this order" — a shortcut for the screen they are standing on. It is an ordinary
   * turn in the conversation, not a special mode: it types the question for them.
   *
   * ⚠️ NO LONGER MANAGER-GATED, AND THE GATE HAS TO STAY OFF. The question resolves to the
   * corpus answer "Why is this production order held up?", which calls exactly three tools —
   * `get_production_order`, `get_production_order_materials`, `get_production_order_notes` —
   * and all three are open to anyone signed in, because all three answer with what is already
   * on the production order details screen. The operator standing at the machine is the person
   * who asks this, and withholding the button from them left it offered only to the people who
   * were not looking at the order.
   */
  const canDiagnoseOrder = Boolean(contextOrder) && !unavailable;

  return (
    <>
      {/* The launcher bubble and the panel share the bottom-right corner and swap: the bubble is
          drawn whenever the panel is closed (see `SupportLauncherButton`). It opens the panel
          with no seed — the same "open, touch nothing" the bus's `showSupportAssistant` gives.

          The drawer still does not close the panel. Once a conversation is open, closing the
          feedback form must not yank it off screen mid-answer — the user closes it themselves. */}
      {!open && shouldShowSupportLauncher() && (
        <SupportLauncherButton onOpen={() => setOpen(true)} offset={isDiagnosticPaneOpen} />
      )}

      {open && (
        <Panel role="dialog" aria-label="CoSMo" $offset={isDiagnosticPaneOpen}>
          <PanelHeader>
            {/* ⚠️ THE HEADING IS THE LOGO, AND `alt` IS WHAT MAKES THAT SAFE. It stays an
                `<h2>` — the panel's heading structure is unchanged, and to a screen reader
                the row still reads "CoSMo, heading level 2", because the image's alt text is
                the heading's accessible name. An `alt=""` here (the rule the launcher's icon
                follows, since its BUTTON carries the name) would empty the heading instead.

                The colours are the point of this asset, so it is an `<img>` with no filter
                on it — see `HeaderLogo`. And it is a file rather than an `e-icons` glyph for
                the reason `Help.svg` and `SupportChat.svg` are: the Syncfusion icon font
                renders blank app-wide. */}
            <h2>
              <HeaderLogo src={COSMO_LOGO} alt="CoSMo" />
            </h2>
            <HeaderActions>
              {/* Hidden rather than disabled on an empty thread — a permanently greyed
                  control on an otherwise empty panel reads as something being broken.

                  `clearChat` is wrapped rather than passed directly: it takes an optional
                  notice, and handing it to onClick would pass the click event as that
                  notice. */}
              {turns.length > 0 && (
                <ClearButton type="button" onClick={() => clearChat()}>
                  Clear chat
                </ClearButton>
              )}
              <CloseButton
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close CoSMo"
              >
                <img src={ICON_X} alt="" aria-hidden="true" />
              </CloseButton>
            </HeaderActions>
          </PanelHeader>

          <Thread ref={threadRef}>
            {/* ⚠️ THIS BRANCHES ON THE ACCESS MAP — THE PERMISSION — AND IT USED
                TO BRANCH ON `isManagerMode`, THE VIEW. A manager standing on the shop-floor
                board was told "I can't look up your data" and then had the lookup work, which
                is how a help panel teaches people not to trust it. What somebody may ask for
                is a fact about them, not about which tab is selected.

                The EXAMPLES are the same question one step earlier: they are what the panel
                advertises itself as being for, so offering "creating a user" to an operator
                points them at a screen they cannot open. The three on the operator's side are
                all `access: "any"` work the answer actions can carry out from here.

                The LOOKUP sentence names only what the server will actually do for that
                caller — see `BotToolset.Build` for the split and the rule behind it. Promising
                a lookup that comes back refused is worse than not mentioning it: the refusal
                reads as a fault. The server is still the thing that enforces it. */}
            {/* ⚠️ `DRAWER_OPEN_NOTICE` IS THE ONE NOTICE THE INTRO SURVIVES. A notice normally
                replaces it because it is about the conversation, and an intro saying "ask me
                something" under a line about what just happened is noise. That one is about the
                PANEL — why it appeared and what it is not — so it answers a different question,
                and the panel it appeared in is empty and needs to say what it is for. Anyone who
                opened the drawer is the least likely person in the app to already know. */}
            {/* ⚠️ AND IT IS WHERE THE NAME IS EXPLAINED. The panel header is the CoSMo logo
                — a name and a mark, which tells somebody meeting it for the first time
                nothing at all about what it DOES; a logo says even less than the bare word
                did. This line is the only place that says what CoSMo is, and it is on screen
                exactly when they have never asked anything. */}
            {turns.length === 0 && (!notice || notice === DRAWER_OPEN_NOTICE) && (
              <Intro>
                I&apos;m CoSMo. Ask me how to do something in CSM —{" "}
                {offersSettingsExamples
                  ? "creating a user, setting up a resource, building a report. "
                  : "starting a job, completing a resource, sending one back for rework. "}
                {lookupSentence}
              </Intro>
            )}

            {turns.map((turn, index) => (
              <React.Fragment key={index}>
                <Bubble $fromUser={turn.role === "user"}>
                  {turn.role === "user" ? (
                    // Literally what they typed. No parsing — an operator who types **this**
                    // means asterisks, and their line breaks are their own.
                    turn.content
                  ) : (
                    <AnswerText
                      content={turn.content}
                      images={turn.images}
                      cards={turn.cards}
                    />
                  )}
                </Bubble>

                {/* Only when the BFF recorded the exchange and gave us its id. No id means
                    nothing was stored (transcripts off, a storage failure, or an older
                    Function App), and a thumb that can only 404 is worse than no thumb. */}
                {turn.role === "assistant" && turn.id && (
                  <RateRow>
                    <RateButton
                      type="button"
                      $active={turn.rating === "up"}
                      aria-pressed={turn.rating === "up"}
                      onClick={() => rate(index, "up")}
                      aria-label="This answer helped"
                      title="This answer helped"
                    >
                      <img src={ICON_THUMB_UP} alt="" aria-hidden="true" />
                    </RateButton>
                    <RateButton
                      type="button"
                      $active={turn.rating === "down"}
                      aria-pressed={turn.rating === "down"}
                      onClick={() => rate(index, "down")}
                      aria-label="This answer did not help"
                      title="This answer did not help"
                    >
                      <img src={ICON_THUMB_DOWN} alt="" aria-hidden="true" />
                    </RateButton>
                  </RateRow>
                )}

                {/* The chips for this answer. Rendered OUTSIDE the bubble: `AnswerText`'s
                    grammar has no way to express a destination, and that is deliberate —
                    see SupportAssistant.styles.ts → ChipRow. */}
                {turn.role === "assistant" && (
                  <>
                    {(() => {
                      const links = linkChipsFor(turn);
                      const actions = actionChipsFor(turn);
                      const settings = settingsChipsFor(turn);
                      // `label` is the target entry's own canonical question, resolved
                      // server-side from `[[next:id]]` — so it is both what the chip reads and
                      // what gets submitted. Anything without one is dropped rather than shown
                      // as an empty chip.
                      const follows = (turn.next ?? []).filter((n) => n?.label?.trim());

                      if (
                        !links.length &&
                        !actions.length &&
                        !settings.length &&
                        !follows.length
                      ) {
                        return null;
                      }

                      return (
                        <ChipRow>
                          {links.map((chip) => (
                            <Chip
                              key={`link-${chip.screen.key}-${chip.href}`}
                              type="button"
                              $primary
                              onClick={() => openScreen(chip.screen, chip.href)}
                            >
                              {`Open ${chip.screen.title}`}
                            </Chip>
                          ))}

                          {actions.map((chip) => (
                            <Chip
                              key={`action-${chip.action.key}-${chip.order}`}
                              type="button"
                              $primary
                              onClick={() => runAction(chip)}
                              // Says what the chip does and, just as importantly, what it does
                              // not do. Nothing is submitted by pressing it. A `caution` is
                              // repeated here as well as in the thread so it is readable
                              // BEFORE the press, not only after it.
                              title={
                                `${chip.action.label} for ${chip.order}. ` +
                                "Nothing is submitted until you complete the form." +
                                (chip.action.caution ? ` ${chip.action.caution}` : "")
                              }
                            >
                              {`${chip.action.label} for ${chip.order}`}
                            </Chip>
                          ))}

                          {settings.map((chip) => (
                            <Chip
                              key={`settings-${chip.settings.key}`}
                              type="button"
                              $primary
                              onClick={() => runSettingsPrefill(chip)}
                              // Says what the chip does and, just as importantly, what it does
                              // not do. The label promises only the filling in; the title
                              // repeats the part that matters BEFORE the press, not only after
                              // it — and names the button, which really is Update by then.
                              title={
                                `${chip.settings.label}. Nothing is saved until you check it ` +
                                "over and press Update."
                              }
                            >
                              {chip.settings.label}
                            </Chip>
                          ))}

                          {follows.map((follow) => (
                            <Chip
                              key={`next-${follow.key}`}
                              type="button"
                              disabled={sending || unavailable}
                              onClick={() => submit(undefined, follow.label)}
                            >
                              {follow.label}
                            </Chip>
                          ))}
                        </ChipRow>
                      );
                    })()}
                  </>
                )}
              </React.Fragment>
            ))}

            {sending && <Bubble aria-live="polite">{progressMessage}</Bubble>}

            {notice && <Notice role="status">{notice}</Notice>}

            {/* Still offered after a thumbs-down: the form opens itself then, and this is how
                the user gets back to it if they close it and change their mind. */}
            {escalation && !sending && (
              <EscalateRow>
                <EscalateButton type="button" onClick={() => openReport(escalation)}>
                  Report this to support
                </EscalateButton>
              </EscalateRow>
            )}
          </Thread>

          {/* About WHERE THEY ARE, not about anything that has been said — so it sits with the
              composer rather than in the thread, and is there before the first question. */}
          {canDiagnoseOrder && (
            <ContextRow>
              <Chip
                type="button"
                disabled={sending}
                onClick={() =>
                  submit(undefined, `Diagnose production order ${contextOrder}`)
                }
              >
                {`Diagnose this order (${contextOrder})`}
              </Chip>
            </ContextRow>
          )}

          <Composer onSubmit={(e) => submit(e, draft.trim())}>
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask a question…"
              aria-label="Ask a question"
              rows={1}
              disabled={unavailable}
            />
            <SendButton type="submit" disabled={!draft.trim() || sending || unavailable}>
              Send
            </SendButton>
          </Composer>

          <Disclaimer>
            Answers come from CSM&apos;s help material and can be wrong. Check anything
            important before you act on it.
          </Disclaimer>
        </Panel>
      )}

      {/* The existing report form, pre-filled. It never sends on the user's behalf. */}
      <DiagnosticModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        initialSubject={report?.subject}
        initialDescription={report?.description}
      />
    </>
  );
};

export default SupportAssistant;
